import { Injectable } from '@angular/core';
import { FormDataType } from 'ngx-extended-pdf-viewer';
import { BehaviorSubject } from 'rxjs';

import {
  IEditDocSingleAuditChange,
  IEditDocumentSingleFormField,
} from '../../interfaces/edit-document.interface';
import { IGetPdfFormFieldsParams } from '../../interfaces/pdf-populate.interface';
import { Acp4Api } from '../acp4-api/acp4-api.service';

@Injectable({
  providedIn: 'root',
})
export class PdfPopulateService {
  /* store form fields data as returned from db temporarily - remove when not needed for peformance reasons */
  formFieldsData: IEditDocumentSingleFormField[] = [];
  summaryData: any = [];
  originalSummaryData: any = [];
  fieldValues: any = {};
  initialFieldValues: any = {};
  formIdToFieldName: any = {};
  pdfPopulatingState = false;
  public formData: BehaviorSubject<FormDataType> = new BehaviorSubject<FormDataType>({});

  constructor(private apiSvc: Acp4Api) {}

  /**
   * populateFormData()
   * iterates through the fieldValues array
   * and assigns the values to the formData object
   * which is bound to the pdf object's form values
   * @returns void
   */
  populateFormData(): void {
    this.pdfPopulatingState = true;
    const formData: any = {};
    for (const field in this.fieldValues) {
      const fieldValue = this.fieldValues[field];
      for (let id in fieldValue.FormFieldName) {
        id = fieldValue.FormFieldName[id];
        const fieldName = this.formIdToFieldName[id];
        if (fieldName) formData[fieldName as string] = fieldValue.DataValue;
      }
    }
    this.formData.next(formData);
    this.pdfPopulatingState = false;
  }

  /**
   * getFormFieldsData() is used to fetch the values to populate form with values from db
   * @param documentInfo contain the bo and doc data that the db requires
   * @returns observable with server response
   */
  getFormFieldsData(documentInfo: IGetPdfFormFieldsParams) {
    return this.apiSvc.post(this.apiSvc.URL_GET_FORM_FIELD_VALUES, documentInfo);
  }

  /**
   * tranformData() access the raw data from the API call to fetch form fields
   * and transforms it into a format that allows the template to display it dynamically
   * @param formFieldsData is a set of data for selected form type fetched from db
   */
  transformData(formFieldsData: IEditDocumentSingleFormField[]): void {
    this.summaryData = this.originalSummaryData = [];
    this.fieldValues = this.initialFieldValues = {};
    // collecting and ordering the sections of the document
    const sectionsSet = new Set(
      formFieldsData.map(function (formField: IEditDocumentSingleFormField) {
        return JSON.stringify({
          SectionName: formField.DocumentSectionName,
          SectionOrder: formField.DocumentSectionOrder,
        });
      }),
    );
    // collecting and ordering the fields in each section
    const sectionOrder: any[] = [...sectionsSet]
      .map((section: string) => JSON.parse(section))
      .sort((a: any, b: any) => (a.SectionOrder > b.SectionOrder ? 1 : -1));

    for (const section of sectionOrder) {
      const fields = formFieldsData
        .filter(
          (result: IEditDocumentSingleFormField) =>
            result.DocumentSectionName === section.SectionName,
        )
        .map((field: IEditDocumentSingleFormField) => {
          field.DataValue = this.updateFormFieldValues(field);
          if (field.DataTypeName == 'DATE')
            if (!field.DataValue || typeof field.DataValue != 'string') field.DataValue = '';

          field.AnchorID = 'Right-' + field.FormFieldName.join(',');
          return field;
        })
        .sort((fieldA: IEditDocumentSingleFormField, fieldB: IEditDocumentSingleFormField) =>
          fieldA.DocumentSectionMappingOrder < fieldB.DocumentSectionMappingOrder ? -1 : 1,
        );

      if (fields.some(field => field.RowNumber > 0))
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.summaryData.push([
          {
            sectionName: section.SectionName,
            isGrid: true,
            rowsCount: this.getRowsCount(fields),
            columnCount: this.getColumnCount(fields),
          },
          this.arrayToObject(fields, 'DataFieldDefinitionID'),
        ]);
      else
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.summaryData.push([
          {
            sectionName: section.SectionName,
          },
          this.arrayToObject(fields, 'DataFieldDefinitionID'),
        ]);
    }
    this.originalSummaryData = [...this.summaryData];
    this.getInitialFieldValues();
  }

  updateFormFieldValues(formField: IEditDocumentSingleFormField) {
    if (formField.DataTypeName == 'BIT' && !formField.DataValue) return false;
    if (formField.DataTypeName == 'STRING' && !formField.DataValue) return '';
    return formField.DataValue;
  }

  getRowsCount(formFields: IEditDocumentSingleFormField[]) {
    return Math.max(...formFields.map(field => field.RowNumber));
  }

  getColumnCount(formFields: IEditDocumentSingleFormField[]) {
    return formFields.filter(field => field.RowNumber === 1).length;
  }

  /**
   * getInitialFieldValues()
   */
  getInitialFieldValues() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.summaryData.forEach((section: any) => {
      this.fieldValues = {
        ...this.fieldValues,
        ...section[1],
      };
    });
    for (const field in this.fieldValues)
      if (this.fieldValues[field].CustomCSS) {
      }

    this.initialFieldValues = JSON.parse(JSON.stringify(this.fieldValues));
  }

  updateFieldValuesWithAuditDetails(details: IEditDocSingleAuditChange[]) {
    details.forEach((change: IEditDocSingleAuditChange) => {
      const { DataValueNew, DataFieldDefinitionID } = change;
      const matchingFieldIdx = this.formFieldsData.findIndex(
        (field: IEditDocumentSingleFormField) =>
          field.DataFieldDefinitionID === DataFieldDefinitionID,
      );
      this.formFieldsData[matchingFieldIdx].DataValue = DataValueNew;
    });
  }

  /**
   * removes all data from the pdf service to prevent premature population of data when opening new pdf
   */
  clearPdfData(): void {
    this.formFieldsData = [];
    this.summaryData = [];
    this.originalSummaryData = [];
    this.fieldValues = {};
    this.initialFieldValues = {};
    this.formIdToFieldName = {};
    this.pdfPopulatingState = false;
    this.formData.next({});
  }

  /**
   * transform an array of objects to an object with a determined key
   * @param array is the array to be converted
   * @param keyName is the name of the prop in the objects to refer in the result object
   * @returns the object
   */
  arrayToObject(array: any[], keyName: string): any {
    const results: any = {};
    for (const [, item] of array.entries()) {
      const key = item[keyName];

      if (results[key])
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        results[key].FormFieldName.push(item.FormFieldName);
      else {
        item.FormFieldName = [item.FormFieldName];
        results[key] = item;
      }
    }

    return results;
  }
}
